// Fonds de secours : passage d'un accordé unique par type de sollicitation (Need.awardedAmountIn* + Need.form)
// à une ventilation par forme d'aide (Need.allocations -> NeedAllocation) :
// - chaque sollicitation accordée reçoit une ligne par forme d'aide proposée pour son unité,
//   le montant accordé étant porté par sa forme actuelle (Directe association par défaut) ;
// - chaque paiement est rattaché à l'allocation de sa sollicitation correspondant à cette forme ;
// - les dossiers sans organisation la récupèrent depuis l'utilisateur déposant.
// Les sollicitations déjà ventilées (allocations présent) et les paiements déjà rattachés sont ignorés.
import {reliefFundFormsByUnit} from "../staticEntities"

const DEFAULT_FORM = 'directAssociationPayment'

async function adaptNeeds() {
    const needCollection = global.app.C.Need.collection
    const allocationCollection = global.app.C.NeedAllocation.collection
    const natureOfHelpCollection = global.app.C.NatureOfHelp.collection

    const needs = await needCollection.find({
        allocations: {$exists: false},
        $or: [{awardedAmountInEuros: {$gt: 0}}, {awardedAmountInKilograms: {$gt: 0}}]
    }).toArray()
    if (!needs.length) return {needsCount: 0, allocationsCount: 0}

    const natureOfHelpIds = [...new Set(needs.map(need => need.natureOfHelp?.toString()).filter(Boolean))]
    const naturesOfHelp = await natureOfHelpCollection.find({_id: {$in: natureOfHelpIds.map(id => new global.ObjectID(id))}}).toArray()
    const unitByNatureOfHelpId = new Map(naturesOfHelp.map(natureOfHelp => [natureOfHelp._id.toString(), natureOfHelp.unit]))

    const allocations = []
    const operations = needs.map(need => {
        const unit = unitByNatureOfHelpId.get(need.natureOfHelp?.toString())
            || (need.awardedAmountInKilograms > 0 ? 'kilogram' : 'euro')
        const awardedAmount = unit === 'kilogram' ? need.awardedAmountInKilograms : need.awardedAmountInEuros
        const forms = reliefFundFormsByUnit[unit] || [DEFAULT_FORM]
        const awardedForm = forms.includes(need.form) ? need.form : DEFAULT_FORM

        const rows = forms.map(form => ({
            _id: new global.ObjectID(),
            form,
            awardedAmount: form === awardedForm ? awardedAmount : 0,
            need: need._id,
            group: need.group
        }))
        allocations.push(...rows)
        return {
            updateOne: {
                filter: {_id: need._id},
                update: {$set: {allocations: rows.map(row => row._id)}}
            }
        }
    })

    await allocationCollection.insertMany(allocations)
    const result = await needCollection.bulkWrite(operations)
    return {needsCount: result.modifiedCount, allocationsCount: allocations.length}
}

async function adaptPayments() {
    const paymentCollection = global.app.C.Payment.collection
    const allocationCollection = global.app.C.NeedAllocation.collection

    const payments = await paymentCollection.find({
        allocation: {$exists: false},
        need: {$exists: true, $ne: null}
    }).toArray()
    if (!payments.length) return 0

    const needIds = [...new Set(payments.map(payment => payment.need.toString()))].map(id => new global.ObjectID(id))
    const allocations = await allocationCollection.find({need: {$in: needIds}, awardedAmount: {$gt: 0}}).toArray()
    const allocationByNeedId = new Map(allocations.map(allocation => [allocation.need.toString(), allocation]))

    const operations = payments
        .filter(payment => allocationByNeedId.has(payment.need.toString()))
        .map(payment => ({
            updateOne: {
                filter: {_id: payment._id},
                update: {$set: {allocation: allocationByNeedId.get(payment.need.toString())._id}}
            }
        }))
    if (!operations.length) return 0

    const result = await paymentCollection.bulkWrite(operations)
    return result.modifiedCount
}

async function adaptReliefFundOrganizations() {
    const reliefFundCollection = global.app.C.ReliefFund.collection
    const cUserCollection = global.app.C.CUser.collection

    const reliefFunds = await reliefFundCollection.find({
        $or: [{organization: {$exists: false}}, {organization: null}],
        submitter: {$exists: true, $ne: null}
    }).toArray()
    if (!reliefFunds.length) return 0

    const submitterIds = [...new Set(reliefFunds.map(reliefFund => reliefFund.submitter.toString()))].map(id => new global.ObjectID(id))
    const cUsers = await cUserCollection.find({kpUser: {$in: submitterIds}, organization: {$exists: true, $ne: null}}).toArray()
    const organizationBySubmitterId = new Map(cUsers.map(cUser => [cUser.kpUser.toString(), cUser.organization]))

    const operations = reliefFunds
        .filter(reliefFund => organizationBySubmitterId.has(reliefFund.submitter.toString()))
        .map(reliefFund => ({
            updateOne: {
                filter: {_id: reliefFund._id},
                update: {$set: {organization: organizationBySubmitterId.get(reliefFund.submitter.toString())}}
            }
        }))
    if (!operations.length) return 0

    const result = await reliefFundCollection.bulkWrite(operations)
    return result.modifiedCount
}

async function applyJob() {
    const {needsCount, allocationsCount} = await adaptNeeds()
    const paymentsCount = await adaptPayments()
    const reliefFundsCount = await adaptReliefFundOrganizations()
    return {
        message: `${needsCount} sollicitation(s) ventilée(s) (${allocationsCount} ligne(s) par forme d'aide), `
            + `${paymentsCount} paiement(s) rattaché(s), ${reliefFundsCount} dossier(s) complété(s) avec l'association`
    }
}

export const job = {
    name: "adaptReliefFundNeedAllocations",
    title: "Fonds de secours : ventiler les montants accordés par forme d'aide",
    execute: function(context, callback) {
        applyJob()
            .then(result => callback(null, result))
            .catch(callback)
    }
}
